<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.fetch_quests', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton :href="meta.links.create" element="inertia-link">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.status') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.group') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.quantity') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(fetchQuest, index) in fetchQuests.data"
                                :key="fetchQuest.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ fetchQuest.status_display }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ fetchQuest.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ fetchQuest.group_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>
                                        {{ fetchQuest.started_at || '-' }}
                                    </div>
                                    <div>
                                        {{ fetchQuest.finished_at || '-' }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ fetchQuest.fetch_quest_actions_count }} /
                                    {{ fetchQuest.quantity_total }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareConfirmActionButton
                                        :href="fetchQuest.links.give_reward"
                                        :disabled="!fetchQuest.links.give_reward"
                                        :title="$t('messages.give_reward')"
                                        method="patch"
                                    >
                                        <template v-slot:icon>
                                            <svg
                                                class="w-4 h-4"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"
                                                ></path>
                                            </svg>
                                        </template>
                                    </InertiaSquareConfirmActionButton>
                                    <InertiaSquareConfirmActionButton
                                        :href="fetchQuest.links.undo"
                                        :disabled="!fetchQuest.links.undo"
                                        :title="$t('messages.undo')"
                                        method="delete"
                                    >
                                        <template v-slot:icon>
                                            <svg
                                                class="w-4 h-4"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z"
                                                ></path>
                                            </svg>
                                        </template>
                                    </InertiaSquareConfirmActionButton>
                                    <InertiaSquareLinkButton action="show" :href="fetchQuest.links.show" />
                                    <InertiaSquareLinkButton action="duplicate" :href="fetchQuest.links.duplicate" />
                                    <InertiaSquareLinkButton action="edit" :href="fetchQuest.links.edit" />
                                    <InertiaSquareDeleteButton
                                        :href="fetchQuest.links.delete"
                                        v-if="fetchQuest.links.delete"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="fetchQuests" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import AppButton from '@/components/AppButton.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        InertiaSquareDeleteButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        InertiaSquareConfirmActionButton,
        AppButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'fetchQuests', 'filters', 'gamificationSet'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
